import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { Wordmark } from "./Wordmark";
import { PastDueBanner } from "./PastDueBanner";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Link2, Video, Send, Settings, LogOut, Building2,
  Menu, X, ChevronDown, Globe, Users, FileText, UserCheck, FileSignature, ClipboardList, LayoutTemplate, HardDrive, History, CreditCard, ListChecks, ShieldCheck, Activity, Stethoscope, Radar, Receipt, CalendarDays, MessageSquare,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const NAV = [
  { group: "Client Work" },
  { to: "/app", label: "Today", icon: LayoutDashboard, end: true },
  { to: "/app/clients", label: "Clients", icon: Users, cap: "clients" },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays, cap: "clients", badge: "review" },
  { to: "/app/clinicals", label: "Clinicals", icon: FileText, cap: "documents_view" },
  { to: "/app/dla20", label: "DLA-20", icon: Activity, cap: "dla20" },
  { to: "/app/referrals", label: "Referrals", icon: Stethoscope, cap: "referrals", badge: "inbox" },
  { to: "/app/sessions", label: "Clinical Rooms", icon: Video },
  { to: "/app/matching", label: "Needs Confirmation", icon: UserCheck, cap: "documents_view", badge: "unmatched", badge2: "inbox" },
  { to: "/app/consent-status", label: "Consent Status", icon: FileSignature, cap: "consent_status" },
  { group: "Operations" },
  { to: "/app/reminders", label: "Text Reminders", icon: MessageSquare, cap: "consent_status" },
  { to: "/app/authorizations", label: "Authorizations", icon: ClipboardList, cap: "clients", badge: "auth" },
  { to: "/app/my-billing", label: "My Billing", icon: Receipt, roles: ["clinician", "intake_staff", "clinical_supervisor"] },
  { to: "/app/biller", label: "Biller Workspace", icon: Receipt, cap: "billing_services", roles: ["platform_admin", "company_admin", "biller", "qa_compliance", "clinical_supervisor"] },
  { to: "/app/billing", label: "Subscription", icon: CreditCard, minRole: "company_admin" },
  { to: "/app/qa", label: "QA", icon: ShieldCheck, cap: "qa", badge: "qa" },
  { to: "/app/runmila", label: "Runmila™", icon: Radar },
  { group: "Management", minRole: "company_admin" },
  { to: "/app/consent-builder", label: "Consent Builder", icon: FileSignature, minRole: "company_admin" },
  { to: "/app/intake-builder", label: "Pre-Intake Builder", icon: ClipboardList, minRole: "company_admin" },
  { to: "/app/referral-builder", label: "Referral Builder", icon: Stethoscope, minRole: "company_admin" },
  { to: "/app/templates", label: "Clinical Templates", icon: LayoutTemplate, minRole: "company_admin" },
  { to: "/app/links", label: "Links & QR", icon: Link2 },
  { to: "/app/audit", label: "Audit History", icon: History, minRole: "company_admin" },
  { to: "/app/settings", label: "Agency Settings", icon: Settings, minRole: "company_admin" },
  { to: "/app/onboarding", label: "Onboarding", icon: ListChecks, minRole: "company_admin" },
  { to: "/app/readiness", label: "Readiness", icon: ShieldCheck, minRole: "company_admin" },
  { to: "/platform", label: "Platform Admin", icon: Globe, platform: true },
];
const PROVIDER_NAV = [
  { group: "Referrals" },
  { to: "/app/referrals", label: "Send a referral", icon: Send, end: true },
  { to: "/app/referrals/workers", label: "Who is seeing my client?", icon: UserCheck },
];
const RANK = { platform_admin: 100, company_admin: 80, clinical_supervisor: 60, clinician: 40, qa_compliance: 30, authorization_staff: 25, biller: 25, intake_staff: 20, referring_provider: 10 };

export default function Layout({ children }) {
  const { user, logout, activeMembership, activeCompanyId, setActiveCompany } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unmatched, setUnmatched] = useState(0);
  const [stale, setStale] = useState(0);
  const [review, setReview] = useState(0);
  const [authAlerts, setAuthAlerts] = useState(0);
  const [qaAlerts, setQaAlerts] = useState(0);
  const canClients = user?.platform_admin || activeMembership?.capabilities?.clients;
  useEffect(() => {
    if (!canClients || !activeCompanyId || activeMembership?.role === "referring_provider") return;
    const pull = () => Promise.all([api.get("/encounters/review-queue"), api.get("/encounters/mine"), api.get("/authorizations/alerts"), api.get("/qa/overview")]).then(([q, m, a, o]) => { setReview(q.data.length + m.data.filter((x) => x.status === "needs_correction").length); setAuthAlerts(a.data.count); setQaAlerts(o.data.action); }).catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    window.addEventListener("encounters-changed", pull);
    return () => { clearInterval(id); window.removeEventListener("encounters-changed", pull); };
  }, [canClients, activeCompanyId, activeMembership?.role]);
  const canTriage = user?.platform_admin || activeMembership?.capabilities?.referral_triage;
  useEffect(() => {
    if (!canTriage || !activeCompanyId) return;
    const pull = () => api.get("/documents/unmatched/summary").then((r) => { setUnmatched(r.data.total); setStale(r.data.stale || 0); }).catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    const onChange = () => pull();
    window.addEventListener("unmatched-changed", onChange);
    return () => { clearInterval(id); window.removeEventListener("unmatched-changed", onChange); };
  }, [canTriage, activeCompanyId]);
  const [inboxCount, setInboxCount] = useState(0);
  useEffect(() => {
    if (!activeCompanyId) return;
    const pull = () => api.get("/referrals/inbox/count").then((r) => setInboxCount(r.data.total || 0)).catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    window.addEventListener("referral-inbox-changed", pull);
    return () => { clearInterval(id); window.removeEventListener("referral-inbox-changed", pull); };
  }, [activeCompanyId]);

  const NavItems = ({ onClick }) => {
    const isProvider = activeMembership?.role === "referring_provider" && !user?.platform_admin;
    const caps = activeMembership?.capabilities;
    const items = (isProvider ? PROVIDER_NAV : NAV).filter((n) => (!n.platform || user?.platform_admin) && (!n.minRole || user?.platform_admin || RANK[activeMembership?.role] >= RANK[n.minRole]) && (!n.roles || user?.platform_admin || n.roles.includes(activeMembership?.role)) && (!n.cap || user?.platform_admin || (caps ? caps[n.cap] : true)));
    return (
    <nav className="flex flex-col divide-y divide-border/70 border-y border-border/70" aria-label="Main">
      {items.map((n, i) => n.group ? <p key={`g-${n.group}`} className="bg-muted/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{n.group}</p> : (
        <NavLink
          key={n.to} to={n.to} end={n.end} onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-[5px] text-[13px] leading-tight transition-colors ${
              isActive
                ? "border-l-[3px] border-primary bg-secondary/70 pl-[9px] font-semibold text-primary"
                : "font-medium text-foreground/80 hover:bg-muted hover:text-foreground"
            }`
          }
        >
          <n.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1">{n.label}</span>
          {n.badge === "unmatched" && canTriage && unmatched > 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${stale > 0 ? "bg-[#A4473C]" : "bg-[#91661E]"}`} title={stale > 0 ? `${stale} waiting 7+ days` : undefined} data-testid="nav-unmatched-badge">{unmatched}</span>}
          {n.badge === "review" && review > 0 && <span className="rounded-full bg-[#A4473C] px-2 py-0.5 text-[11px] font-bold text-white" title="Notes awaiting review or correction" data-testid="nav-review-badge">{review}</span>}
          {(n.badge === "inbox" || n.badge2 === "inbox") && inboxCount > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-white" title="Referrals received, not yet filed in a chart" data-testid={`inbox-badge-${n.label.replace(/\s+/g, "-").toLowerCase()}`}>{inboxCount}</span>}
          {n.badge === "auth" && authAlerts > 0 && <span className="rounded-full bg-[#91661E] px-2 py-0.5 text-[11px] font-bold text-white" title="Authorizations expiring / expired / duplicate referrals" data-testid="nav-auth-badge">{authAlerts}</span>}
          {n.badge === "qa" && qaAlerts > 0 && <span className="rounded-full bg-[#A4473C] px-2 py-0.5 text-[11px] font-bold text-white" title="Chart items needing action" data-testid="nav-qa-badge">{qaAlerts}</span>}
          {n.soon && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Soon</span>}
        </NavLink>
      ))}
    </nav>
    );
  };

  const CompanySwitcher = () => (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-2 py-1.5" data-testid="active-company">
      <Building2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="block truncate text-xs font-bold">{activeMembership?.company_name || "No agency"}</span>
    </div>
  );
  const _LegacySwitcher = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="active-company-switcher"
          className="flex w-full items-center justify-between gap-2 rounded-xl border-2 border-primary/20 bg-primary/5 px-3 py-2.5 text-left"
        >
          <span className="flex items-center gap-2 overflow-hidden">
            <Building2 className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span className="overflow-hidden">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Active Company
              </span>
              <span className="block truncate text-sm font-bold text-foreground">
                {activeMembership?.company_name || "No company"}
              </span>
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="start">
        <DropdownMenuLabel>Switch company context</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user?.memberships?.map((m) => (
          <DropdownMenuItem
            key={m.company_id}
            data-testid={`company-option-${m.company_id}`}
            onClick={() => { setActiveCompany(m.company_id); window.location.reload(); }}
            className="flex flex-col items-start"
          >
            <span className="font-semibold">{m.company_name}</span>
            <span className="text-xs text-muted-foreground">
              {m.role.replace(/_/g, " ")} {m.company_id === activeCompanyId ? "· active" : ""}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col gap-3 overflow-y-auto border-r border-border bg-card px-3 py-3 lg:flex">
        <Wordmark className="text-lg" />
        <CompanySwitcher />
        <NavItems />
        <p className="mt-auto px-1 text-[10px] text-muted-foreground">© {new Date().getFullYear()} Airfistuus™</p>
      </aside>

      {/* Mobile header */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-border bg-card px-4 py-2.5" data-testid="top-bar">
          <div className="flex items-center gap-3"><Wordmark className="text-lg lg:hidden" /><span className="hidden text-sm text-muted-foreground lg:block">{activeMembership?.company_name}</span></div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-3" data-testid="user-menu">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">{(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}</span>
              <span className="leading-tight"><span className="block max-w-[11rem] truncate text-xs font-bold" data-testid="user-name">{user?.name || user?.email}</span><span className="block text-[10px] capitalize text-muted-foreground">{activeMembership?.role?.replace(/_/g, " ")}</span></span>
            </div>
            <Button size="sm" variant="outline" className="h-9 rounded-full" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button"><LogOut className="mr-1 h-4 w-4" /> Logout</Button>
            <Button size="icon" variant="ghost" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="h-6 w-6" /></Button>
          </div>
        </header>

        {open && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-72 space-y-6 bg-card p-5">
              <div className="flex items-center justify-between">
                <span className="font-head text-lg font-extrabold">Airfistuus</span>
                <Button size="icon" variant="ghost" onClick={() => setOpen(false)} aria-label="Close menu">
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <CompanySwitcher />
              <NavItems onClick={() => setOpen(false)} />
              <Button variant="outline" className="w-full" onClick={() => { logout(); navigate("/login"); }}>
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </Button>
            </div>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <PastDueBanner membership={activeMembership} isAdmin={user?.platform_admin || RANK[activeMembership?.role] >= RANK.company_admin} />
          <main className="p-5 sm:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
